import { Suspense, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { ButtonLoading } from "src/components/ButtonLoading";
import { ResidentialZoneRestrictionsTable } from "./ResidentialZoneRestrictionsTable";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { IsDisabled, IsEnabled } from "src/components/permissions";
import { useScenario } from "src/hooks/useScenario";
import { RestrictionsMutationData, useResidentialZoneRestrictionsUpdateMutation } from "src/hooks/residential-zones";
import { isDisabled } from "src/utils/permission.utils";
import { ResidentialZoneRestrictionsUploadDialog } from "src/components/ResidentialZoneRestrictionsUploadDialog";
import { ResidentialZoneWeightItem } from "src/types";
interface Props {
  scenarioId: number | string;
  residentialZoneId: number | string;
  onClose: () => void;
}

export function ResidentialZoneRestrictionsDialog(props: Props) {
  const { scenarioId, onClose, residentialZoneId } = props;
  const { data: scenario } = useScenario(scenarioId);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadWeightList, setUploadWeightList] = useState<ResidentialZoneWeightItem[]>();
  const [uploadNumber, setUploadNumber] = useState(0);
  const { t } = useTranslation();

  const updateMutation = useResidentialZoneRestrictionsUpdateMutation(scenarioId, residentialZoneId);

  async function handleSave(data: RestrictionsMutationData) {
    await updateMutation.mutateAsync(data);
    onClose();
  }

  function incrementUploadNumber() {
    setUploadNumber((prev) => prev + 1);
  }

  if (!scenario) return null;

  return (
    <StyledDialog fullWidth maxWidth="md" open={true}>
      <DialogContent>
        <Typography variant="h2" gutterBottom>
          {t("label.residentialZones")}
        </Typography>
        <Suspense fallback={<FleetConfigLoading />}>
          <ResidentialZoneRestrictionsTable
            scenario={scenario}
            residentialZoneId={residentialZoneId}
            readOnly={isDisabled(scenario.permissions.updateResidential)}
            onChange={handleSave}
            uploadWeightList={uploadWeightList}
            uploadNumber={uploadNumber}
          />
        </Suspense>
      </DialogContent>
      <DialogActions>
        {isUploadDialogOpen && (
          <ResidentialZoneRestrictionsUploadDialog
            residentialZoneId={residentialZoneId}
            onClose={() => setIsUploadDialogOpen(false)}
            open={isUploadDialogOpen}
            setUploadWeightList={setUploadWeightList}
            incrementUpload={incrementUploadNumber}
          />
        )}

        {updateMutation.error?.response?.data && <ServerErrors data={updateMutation.error.response.data} />}
        <IsDisabled permission={scenario.permissions.updateResidential}>
          <Button onClick={onClose} color="primary">
            {t("label.close")}
          </Button>
        </IsDisabled>
        <IsEnabled permission={scenario.permissions.updateResidential}>
          <Button onClick={() => setIsUploadDialogOpen(true)} color="primary">
            {t("label.uploadFile")}
          </Button>
          <Button onClick={onClose} color="primary">
            {t("label.cancel")}
          </Button>
          <ButtonLoading isLoading={updateMutation.isLoading} form="weight-form" type="submit">
            {t("label.save")}
          </ButtonLoading>
        </IsEnabled>
      </DialogActions>
    </StyledDialog>
  );
}

const StyledDialog = styled(Dialog)`
  --text-field-width: 100px;

  .MuiDialogActions-root {
    padding: ${({ theme }) => theme.spacing(3)};
  }

  .MuiDialogContent-root {
    display: flex;
    flex-direction: column;
    overflow: auto;
    /* max-height: 900px; */
  }

  .energy-efficiency-measure {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: ${({ theme }) => theme.spacing(2)};
    margin-bottom: ${({ theme }) => theme.spacing(2)};
    background-color: #f7f7f7;
    border: 1px solid #e0e0e0;

    .MuiTypography-root,
    .MuiTextField-root {
      flex-shrink: 0;
    }

    .MuiTextField-root {
      width: var(--text-field-width);
      margin-right: ${({ theme }) => theme.spacing(1)};
    }
    .MuiInputBase-root {
      margin-bottom: 0;
    }
  }
`;

const FleetConfigLoading = styled.div`
  height: 100vh;
`;
